import { List, ListItem } from 'framework7-react';
import { FC, useLayoutEffect, useRef, useState } from 'react';

import { create } from 'cypress/types/lodash';
import * as dayjs from 'dayjs';

import { useCurrentEntertainerQuery } from '@queries/EntertainerQueries';
import {
  useCurrentUserEventRsvp,
  useEventCritiqueDataQuery,
  useEventInterestDataQuery,
  useEventQuery,
} from '@queries/EventQueries';
import { FeedPost, useEventCommentsQuery } from '@queries/PostQueries';
import { useCurrentUser } from '@queries/UserQueries';

import {
  CritiqueDocumentMutationPayload,
  useCritiqueDocumentMutation,
} from '@mutations/CritiqueMutations';
import { RsvpToEventPayload, useRsvpToEventMutation } from '@mutations/EventMutations';
import { useCreateEventComment } from '@mutations/PostMutations';

import { useTipEntertainer } from '@hooks/EntertainerHooks/useTipEntertainer';
import { useAnalytics } from '@hooks/useAnalytics';
import { useReferredBy } from '@hooks/useReferredBy';
import { useShareLink } from '@hooks/useShareLink';

import { useLocation, useNavigate, useParams } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { getEntertainerSeoLink } from '@utilities/entertainerUtilities/getEntertainerSeoLink';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import { DynamicActionButton } from '@components/buttons/DynamicActionButton';
import IconButton from '@components/buttons/IconButton';
import { SendEntertainerBadgeButton } from '@components/buttons/SendEntertainerBadgeButton';
import { EventActions, EventHeader, EventImage, PromoterHeader } from '@components/cards/EventCard';
import { PostCard } from '@components/cards/PostCard/PostCard';
import { Textarea } from '@components/form/Textarea';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { CritiqueSection } from '@components/layout/DetailsChartSection/CritiqueSection';
import { EventInterestSection } from '@components/layout/DetailsChartSection/EventInterestSection';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { Screen } from '@components/layout/Screen/Screen';
import { SeoLink } from '@components/layout/SeoLink/SeoLink';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';
import { PrimaryCritiqueCountChips } from '@components/overlays/CritiqueOverlay/PrimaryCritiqueCountChips';
import { ReportOverlay } from '@components/overlays/ReportOverlay/ReportOverlay';

import { VenmoLogo } from '../../../assets/VenmoLogo';
import fireIconUrl from '../../CommunityScreen/assets/fire.svg';
import { useJoinLineup } from '../../CommunityScreen/components/ShowsTabContent';
import { EventCleanupPopup } from '../../EventCleanupScreen';
import { useAuthGate } from '../../LogInScreen/AuthGateContext';
import { CastReferralPopup } from './CastReferralPopup';
import './EventScreen.scss';

const EventCommentSection: FC<{
  eventId: string;
  commentFieldIsVisible: boolean;
  setCommentFieldIsVisible: (updatedValue: boolean) => void;
}> = ({ eventId, commentFieldIsVisible, setCommentFieldIsVisible }) => {
  const [commentText, setCommentText] = useState<string>('');

  const { createAuthGateHandler } = useAuthGate();
  const { eventComments = [], eventCommentsAreReady } = useEventCommentsQuery(eventId);
  const { isSubmitting, createEventCommentAsync } = useCreateEventComment(eventId);

  const handleSubmitComment = async () => {
    if (!commentText || isSubmitting) {
      return;
    }

    createAuthGateHandler(async () => {
      await createEventCommentAsync({ text: commentText });

      setCommentText('');
    });
  };

  return (
    <>
      <div id="event-comments" />
      <TitleToolbar
        renderAs="h3"
        size="md"
        text="Comments"
        action={
          <Button disabled={false} variant="flat" onClick={() => setCommentFieldIsVisible(true)}>
            Add Comment
          </Button>
        }
      />
      {commentFieldIsVisible && (
        <Textarea
          onChange={setCommentText}
          submitButton={{
            text: 'Comment',
            disabled: isSubmitting,
            onClick: handleSubmitComment,
          }}
        />
      )}

      {eventCommentsAreReady && (
        <>
          {eventComments.length ? (
            <div className="comments">
              {eventComments.map((comment) => (
                <PostCard key={comment._id} post={comment} showDetails={false} />
              ))}
            </div>
          ) : (
            <EmptyState
              className="comments-empty-state"
              title="No Comments Yet"
              text="Be the first to comment or ask a question about this event."
              button={{
                text: 'Add Comment',
                onClick: () => setCommentFieldIsVisible(true),
              }}
            />
          )}
        </>
      )}
    </>
  );
};

interface EventScreenProps {
  back?: string;
}

export const TipEntertainerButton: FC<{ entertainer: any }> = ({ entertainer }) => {
  const { handleVenmoEntertainer, tipEntertainerPopup } = useTipEntertainer();
  const handleTip = () => {
    handleVenmoEntertainer({
      entertainerId: entertainer._id,
      venmo: entertainer?.contact?.venmo,
    });
  };

  return (
    <>
      <Button
        size="sm"
        roundness="rounded"
        style={{
          borderRadius: 4,
        }}
        startIcon={
          <div style={{ width: 14, height: 14 }}>
            <VenmoLogo />
          </div>
        }
        onClick={handleTip}
      >
        Tip
      </Button>
      {tipEntertainerPopup}
    </>
  );
};

const IS_CONFIRMATION_ENABLED = false;

export const UnconfirmedEntertainerButton: FC<{
  entertainer: { _id: string; title: string; contact?: { instagram?: string } };
  eventId: string;
  onJoinLineup: () => void;
}> = ({ eventId, entertainer, onJoinLineup }) => {
  const url = 'https://ig.me/m/gaggl.app';

  const { openAlertDialog, openConfirmDialog } = useDialog();

  const code = '0000';

  const handleConfirm = () => {
    if (IS_CONFIRMATION_ENABLED) {
      openConfirmDialog({
        title: 'Is this you?',
        text: `Only ${entertainer.title} can confirm themselves in the cast list. If you are ${entertainer.title}, please confirm your account by clicking the button below.`,
        onConfirm: async () => {
          // await copyTextToClipboard(code);
          openAlertDialog({
            title: 'Confirm with Instagram',
            text: `We copied the code "${code}" to your clipboard. Use the button below to open a message with us at @gaggl.app and send the code to confirm your account.`,
            cb: () => {
              openExternalUrl(url);
            },
          });
        },
        buttonOk: 'Confirm',
        buttonCancel: 'Not me',
      });
    } else {
      onJoinLineup();
    }
  };

  if (!entertainer.contact?.instagram) {
    return <></>;
  }

  return (
    <Button
      size="sm"
      roundness="rounded"
      color="danger"
      variant="smooth"
      style={{
        borderRadius: 4,
      }}
      startIcon={<FontAwesomeIcon name="info-circle" />}
      onClick={handleConfirm}
    >
      Unconfirmed
    </Button>
  );
};

const useEventRsvpActions = (eventId: string) => {
  const { rsvpToEventAsync, isSubmitting } = useRsvpToEventMutation();
  const { refetchEventInterestData } = useEventInterestDataQuery(eventId);

  const handleMarkAttending = async () => {
    await rsvpToEventAsync({
      eventId,
      rsvp: 'yes',
    });
    refetchEventInterestData();
  };

  const handleMarkNotAttending = async () => {
    await rsvpToEventAsync({
      eventId,
      rsvp: 'no',
    });
    refetchEventInterestData();
  };

  const handleMarkInterested = async () => {
    await rsvpToEventAsync({
      eventId,
      eventInterest: 'interested',
    });
    refetchEventInterestData();
  };

  const handleMarkNotInterested = async () => {
    await rsvpToEventAsync({
      eventId,
      eventInterest: 'notInterested',
    });
    refetchEventInterestData();
  };

  return {
    handleMarkAttending,
    handleMarkNotAttending,
    handleMarkInterested,
    handleMarkNotInterested,
    isSubmitting,
  };
};

const ShareEventButton = ({ event }: { event }) => {
  const { didCopy, shareLink } = useShareLink();

  const handleShare = () => {
    shareLink({
      title: event.title,
      url: `https://my.gaggl.app/events/${event._id}`,
    });
  };

  return (
    <Button
      roundness="rounded"
      variant="smooth"
      color="neutral"
      startIcon={<FontAwesomeIcon name="share" />}
      onClick={handleShare}
    >
      {didCopy ? 'Copied!' : 'Share Event'}
    </Button>
  );
};

export const EventScreen = createScreenComponent<{ eventId: string }>(({ eventId, back }) => {
  const { referredBy } = useReferredBy();
  const { track } = useAnalytics();
  const { didInitialEventFetch, event, entertainerEventReferralUrl, refetchEvent } =
    useEventQuery(eventId);
  const { rsvp } = useCurrentUserEventRsvp(eventId);
  const { eventCritiqueData, refetchEventCritiqueData } = useEventCritiqueDataQuery(eventId);
  const { eventInterestData, refetchEventInterestData } = useEventInterestDataQuery(eventId);
  const { currentUser } = useCurrentUser();
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const [commentFieldIsVisible, setCommentFieldIsVisible] = useState<boolean>(false);
  const [reportOverlayIsVisible, setReportOverlayIsVisible] = useState(false);
  const navigate = useNavigate();
  const { createAuthGateHandler } = useAuthGate();

  const { rsvpToEventAsync, isSubmitting } = useRsvpToEventMutation();
  const { critiqueDocumentAsync } = useCritiqueDocumentMutation();

  const handleScrollToComments = () => {
    document.getElementById('event-comments')?.scrollIntoView({ behavior: 'smooth' });
    setCommentFieldIsVisible(true);
  };

  const handleUpdateCritique = async (payload: CritiqueDocumentMutationPayload) => {
    await critiqueDocumentAsync({
      ...payload,
      documentType: 'events',
      documentId: eventId,
    });
    refetchEventCritiqueData();
  };

  const handleUpdateEventInterest = async (payload: RsvpToEventPayload) => {
    await rsvpToEventAsync({
      ...payload,
      eventId,
    });
    refetchEventInterestData();
  };

  const {
    handleMarkInterested,
    handleMarkNotInterested,
    handleMarkAttending,
    handleMarkNotAttending,
  } = useEventRsvpActions(eventId);

  const attendingToolbarButtons = (
    <>
      <div className="w-50">
        <Button
          disabled={isSubmitting}
          noWrap
          roundness="rounded"
          color="neutral"
          variant={rsvp?.rsvp === 'no' ? 'default' : 'outlined'}
          startIcon={
            <FontAwesomeIcon faStyle="far" name={rsvp?.rsvp === 'no' ? 'times-circle' : 'circle'} />
          }
          onClick={handleMarkNotAttending}
        >
          Not attending
        </Button>
      </div>
      <div className="w-50">
        <Button
          noWrap
          roundness="rounded"
          disabled={isSubmitting}
          variant={rsvp?.rsvp === 'yes' ? 'default' : 'outlined'}
          startIcon={
            <FontAwesomeIcon
              faStyle="far"
              name={rsvp?.rsvp === 'yes' ? 'check-circle' : 'circle'}
            />
          }
          onClick={handleMarkAttending}
        >
          Attending
        </Button>
      </div>
    </>
  );

  const interestToolbarButtons = (
    <>
      <div className="w-50">
        <Button
          disabled={isSubmitting}
          noWrap
          roundness="rounded"
          color="neutral"
          variant={rsvp?.eventInterest === 'notInterested' ? 'default' : 'outlined'}
          startIcon={
            <FontAwesomeIcon
              faStyle="far"
              name={rsvp?.eventInterest === 'notInterested' ? 'times-circle' : 'circle'}
            />
          }
          onClick={handleMarkNotInterested}
        >
          Not interested
        </Button>
      </div>
      <div className="w-50">
        <Button
          noWrap
          roundness="rounded"
          disabled={isSubmitting}
          variant={rsvp?.eventInterest === 'interested' ? 'default' : 'outlined'}
          startIcon={
            <FontAwesomeIcon
              faStyle="far"
              name={rsvp?.eventInterest === 'interested' ? 'check-circle' : 'circle'}
            />
          }
          onClick={handleMarkInterested}
        >
          Interested
        </Button>
      </div>
    </>
  );

  const userDidHype = rsvp?.eventInterest === 'hyped';
  const isPastEvent =
    event && dayjs().isAfter(`${event?.startDate} ${event?.startTime ?? '00:00'}`);
  const handleHypeEvent = () => {
    track('Click Hype Event', { eventId: event._id });
    createAuthGateHandler(async () => {
      await rsvpToEventAsync({
        eventId: event._id,
        eventInterest: userDidHype ? null : 'hyped',
      });
    });
  };

  const { handleJoinLineup } = useJoinLineup({
    primaryEntertainerType: 'dragPerformer',
    cityId: event?.cityId,
    referredBy,
  });

  const didInitCastReferralPopup = useRef(false);
  const [isCastReferralPopupVisible, setIsCastReferralPopupVisible] = useState(false);
  useLayoutEffect(() => {
    if (!didInitCastReferralPopup.current && !!event) {
      didInitCastReferralPopup.current = true;
      setIsCastReferralPopupVisible(!!entertainerEventReferralUrl);
    }
  }, [event, entertainerEventReferralUrl]);

  return (
    <Screen
      className="event-screen"
      name="Event Screen"
      disableLocationPermissionGate
      headerProps={{
        back: back ? { to: back } : undefined,
        title: 'Event Details',
        right: (
          <IconButton
            disabled={false}
            variant="flat"
            color="danger"
            onClick={() => setReportOverlayIsVisible(true)}
          >
            <FontAwesomeIcon faStyle="far" name="flag" />
          </IconButton>
        ),
      }}
      bottomToolbar={
        !isPastEvent && (
          <Screen.BottomToolbar>
            <div className="d-flex gap-2 w-100">
              <Button
                roundness="rounded"
                variant="smooth"
                color="neutral"
                startIcon={
                  <div>
                    <img
                      src={fireIconUrl}
                      alt="Fire Icon"
                      style={{
                        width: 20,
                        height: 20,
                        display: 'block',
                        filter: userDidHype ? undefined : 'grayscale(1)',
                      }}
                    />
                  </div>
                }
                onClick={handleHypeEvent}
              >
                {userDidHype ? 'Event Hyped!' : 'Hype Event'}
              </Button>
            </div>
          </Screen.BottomToolbar>
        )
      }
    >
      <Screen.Content>
        {didInitialEventFetch ? (
          event && (
            <>
              <PromoterHeader event={event} />
              <EventImage event={event} />
              <EventHeader event={event} subtitleDirection="column" />
              {currentUser?.isSuperAdmin && (
                <EventActions
                  event={event}
                  onCommentClick={handleScrollToComments}
                  showCleanup={currentUser?.isSuperAdmin}
                />
              )}
              <ShareEventButton event={event} />
              <TitleToolbar renderAs="h3" size="md" text="About This Event" />
              <div className="event-description">
                {(event.excerpt ?? event.description) ? (
                  mapStringToParagraphs(event.excerpt ?? event.description)
                ) : (
                  <DynamicActionButton
                    variant="smooth"
                    color="neutral"
                    roundness="rounded"
                    actionProperties={{
                      action: 'SplashNotification::EventDescription',
                      payload: { eventId: event._id },
                    }}
                    track={{
                      event: 'Click add event description',
                      properties: {
                        eventId: event._id,
                      },
                    }}
                  >
                    Add Event Description
                  </DynamicActionButton>
                )}
              </div>

              <TitleToolbar size="md" text="Entertainers 💃🏽" />
              {event?.entertainers?.length ? (
                <>
                  <List
                    className="mt-0 mb-2"
                    style={{ marginLeft: ' -1rem', marginRight: '-1rem' }}
                  >
                    {event?.entertainers?.map((entertainer) => (
                      <ListItem key={entertainer._id}>
                        <div
                          slot="title"
                          onClick={() => {
                            if (entertainer.bookingHandle) {
                              navigate(`/noauth/entertainer/${entertainer.bookingHandle}`);
                            }
                          }}
                        >
                          <SeoLink href={getEntertainerSeoLink(entertainer)}>
                            {entertainer.title}
                          </SeoLink>
                        </div>

                        {isPastEvent && (
                          <div slot="root">
                            <div className="px-3 mb-3">
                              <SendEntertainerBadgeButton
                                entertainerId={entertainer._id}
                                eventId={eventId}
                                buttonProps={{
                                  size: 'sm',
                                  className: 'px-3 py-2',
                                  roundness: 'rounded',
                                  variant: 'smooth',
                                  color: 'neutral',
                                }}
                              />
                            </div>
                          </div>
                        )}

                        <div slot="media">
                          {entertainer.contact?.instagram && (
                            <IconButton
                              variant="flat"
                              color="neutral"
                              style={{
                                borderRadius: 4,
                                fontSize: 18,
                                height: 22,
                                width: 22,
                              }}
                              onClick={() => {
                                openExternalUrl(
                                  `https://instagram.com/${entertainer.contact?.instagram}`,
                                );
                              }}
                            >
                              <FontAwesomeIcon faStyle="fab" name="instagram" />
                            </IconButton>
                          )}
                        </div>
                        <div slot="after">
                          <div className="d-flex gap-2">
                            {entertainer.status !== 'pending' ? (
                              entertainer.contact?.venmo && (
                                <TipEntertainerButton entertainer={entertainer} />
                              )
                            ) : (
                              <UnconfirmedEntertainerButton
                                entertainer={entertainer}
                                eventId={eventId}
                                onJoinLineup={() => handleJoinLineup(eventId, () => refetchEvent())}
                              />
                            )}
                            {/* <PrimaryCritiqueCountChips
                          hideCounts
                          disableOverlay
                          controlSelectedRating
                          defaultValue={entertainer.primaryRating}
                          counts={{
                            like: 0,
                            dislike: 0,
                            neutral: 0,
                          }}
                          onSubmit={async () => {}}
                        /> */}
                          </div>
                        </div>
                      </ListItem>
                    ))}
                  </List>
                  <div className="mb-2">
                    <Button
                      variant="smooth"
                      color="neutral"
                      roundness="rounded"
                      onClick={() => handleJoinLineup(eventId, () => refetchEvent())}
                    >
                      Missing from the cast list?
                    </Button>
                  </div>
                </>
              ) : (
                <EmptyState
                  title="No Cast Listed"
                  text="No entertainers have been listed for this event. Are you a part of the cast? Let everyone know with the button below."
                  button={{
                    text: 'Add Me to This Lineup',
                    onClick: () => handleJoinLineup(eventId, () => refetchEvent()),
                    variant: 'smooth',
                    color: 'neutral',
                    roundness: 'rounded',
                  }}
                />
              )}

              {isPastEvent && eventCritiqueData && (
                <>
                  <TitleToolbar renderAs="h3" size="md" text="Critiques 💅🏽" />
                  <CritiqueSection
                    critiqueData={eventCritiqueData}
                    onSubmit={handleUpdateCritique}
                  />
                </>
              )}

              {eventInterestData && (
                <>
                  <TitleToolbar renderAs="h3" size="md" text="Hot Takes 🔥" />
                  <EventInterestSection
                    showShareButton={!isPastEvent}
                    currentUserRsvp={rsvp}
                    interestData={eventInterestData}
                    onSubmit={handleUpdateEventInterest}
                  />
                </>
              )}

              <EventCommentSection
                eventId={eventId}
                commentFieldIsVisible={commentFieldIsVisible}
                setCommentFieldIsVisible={setCommentFieldIsVisible}
              />
            </>
          )
        ) : (
          <div className="d-grid gap-1">
            <Skeleton height={200} />
            <Skeleton height={30} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
          </div>
        )}
      </Screen.Content>
      <ReportOverlay
        isVisible={reportOverlayIsVisible}
        title="Report Event"
        content={<p>Are you sure you want to report this event?</p>}
        data={{
          collectionName: 'event',
          objectId: eventId,
        }}
        onClose={() => setReportOverlayIsVisible(false)}
      />
      <CastReferralPopup
        isVisible={isCastReferralPopupVisible}
        onClose={() => {}}
        entertainers={event?.entertainers
          ?.filter(
            (entertainer) =>
              entertainer.status === 'pending' && entertainer._id !== currentEntertainer?._id,
          )
          ?.map((entertainer) => ({
            _id: entertainer._id,
            title: entertainer.title,
            contact: entertainer.contact,
          }))}
        referralUrl={entertainerEventReferralUrl}
      />
    </Screen>
  );
});
